/**
 * BakenTab.jsx – Backen-Planung (vollständig überarbeitet)
 *
 * Features:
 * 1. Programme: Bearbeiten (Name) + Drag&Drop Reihenfolge
 * 2. Chargen: Drag&Drop Reihenfolge (sofort, innerhalb + zwischen Öfen)
 * 3. Fertig-Haken: optionaler Klick pro Charge mit Name/Uhrzeit
 * 4. Tages-Reset: konfigurierbare Start/Ende-Zeiten (baking_settings)
 * 5. Öfen als Kategorien-Abschnitte im Konfigurationsbereich
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  closestCorners, useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy,
  useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Plus, Trash2, Pencil, X, Check, Flame, MapPin,
  Settings, ChevronDown, ChevronUp, Tag, AlertCircle,
  Loader2, GripVertical, CheckCircle2, Clock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const WOCHENTAGE = [
  { key: "montag",     short: "Mo" },
  { key: "dienstag",   short: "Di" },
  { key: "mittwoch",   short: "Mi" },
  { key: "donnerstag", short: "Do" },
  { key: "freitag",    short: "Fr" },
  { key: "samstag",    short: "Sa" },
  { key: "sonntag",    short: "So" },
];

const WOCHENTAG_DE = {
  montag:"Montag", dienstag:"Dienstag", mittwoch:"Mittwoch",
  donnerstag:"Donnerstag", freitag:"Freitag", samstag:"Samstag", sonntag:"Sonntag",
};

// ── Baking-Day-Logik ──────────────────────────────────────────────────────────

function parseBakingDayStart(resetStart = "04:00") {
  const [h, m] = resetStart.split(":").map(Number);
  const now = new Date();
  const todayStart = new Date(now);
  todayStart.setHours(h, m, 0, 0);
  if (now >= todayStart) return todayStart;
  const yesterday = new Date(todayStart);
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday;
}

function isForceReset(resetEnd = "12:00") {
  const [h, m] = resetEnd.split(":").map(Number);
  const now = new Date();
  const end = new Date(now);
  end.setHours(h, m, 0, 0);
  return now >= end;
}

function isCompletionCurrent(completedAt, bakingDayStart, forceReset) {
  if (forceReset) return false;
  return new Date(completedAt) >= bakingDayStart;
}

function fmtTime(isoStr) {
  if (!isoStr) return '';
  try {
    return new Date(isoStr).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

// ── LocationSelector ──────────────────────────────────────────────────────────
function LocationSelector({ locations, value, onChange }) {
  if (!locations.length) return null;
  const cur = locations.find(l => l.id === value);
  if (locations.length === 1) return (
    <div className="flex items-center gap-1.5 px-3 h-8 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-400">
      <MapPin className="h-3.5 w-3.5 shrink-0" />
      <span className="font-medium">{cur?.name || '–'}</span>
    </div>
  );
  return (
    <div className="flex items-center gap-1.5">
      <MapPin className="h-3.5 w-3.5 text-amber-500 shrink-0" />
      <select value={value} onChange={e => onChange(e.target.value)}
        className="h-8 rounded-md border border-input bg-background px-3 text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none"
        data-testid="baking-location-selector">
        {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
      </select>
    </div>
  );
}

// ── ItemsEditor ───────────────────────────────────────────────────────────────
function ItemsEditor({ items, onChange }) {
  const addItem = () => onChange([...items, { product_name: '', quantity: '' }]);
  const removeItem = i => onChange(items.filter((_, j) => j !== i));
  const updateItem = (i, field, val) => {
    const n = [...items]; n[i] = { ...n[i], [field]: val }; onChange(n);
  };
  return (
    <div className="space-y-1.5">
      {items.map((item, i) => (
        <div key={i} className="flex gap-1.5 items-center">
          <Input value={item.product_name} onChange={e => updateItem(i, 'product_name', e.target.value)}
            placeholder="Produkt" className="flex-1 h-8 text-sm" data-testid={`item-name-${i}`} />
          <Input value={item.quantity ?? ''} onChange={e => updateItem(i, 'quantity', e.target.value)}
            type="number" min="0" step="0.5" placeholder="Menge"
            className="w-20 h-8 text-sm shrink-0" data-testid={`item-qty-${i}`} />
          <button type="button" onClick={() => removeItem(i)} className="text-muted-foreground hover:text-destructive shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button type="button" onClick={addItem}
        className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium">
        <Plus className="h-3 w-3" /> Produkt hinzufügen
      </button>
    </div>
  );
}

// ── ChargeForm ────────────────────────────────────────────────────────────────
function ChargeForm({ ovens, programs, existingCharge, onSave, onCancel }) {
  const [ovenId, setOvenId] = useState(existingCharge?.oven_id || ovens[0]?.id || '');
  const [programId, setProgramId] = useState(existingCharge?.program_id || programs[0]?.id || '');
  const [weekdays, setWeekdays] = useState(existingCharge?.weekdays || []);
  const [items, setItems] = useState(
    existingCharge?.items?.map(i => ({ ...i, quantity: i.quantity ?? '' })) || [{ product_name: '', quantity: '' }]
  );
  const [saving, setSaving] = useState(false);

  // Race-Condition-Fix
  useEffect(() => { if (!ovenId && ovens.length > 0) setOvenId(ovens[0].id); }, [ovens]);
  useEffect(() => { if (!programId && programs.length > 0) setProgramId(programs[0].id); }, [programs]);

  const toggleDay = key => setWeekdays(prev =>
    prev.includes(key) ? prev.filter(d => d !== key) : [...prev, key]
  );

  const handleSave = async () => {
    if (!ovenId) { toast.error('Ofen wählen'); return; }
    if (!programId) { toast.error('Programm wählen'); return; }
    if (!weekdays.length) { toast.error('Mindestens einen Wochentag wählen'); return; }
    const validItems = items.filter(i => i.product_name?.trim()).map(i => ({
      product_name: i.product_name.trim(),
      quantity: i.quantity !== '' ? parseFloat(i.quantity) : null,
    }));
    setSaving(true);
    try { await onSave({ oven_id: ovenId, program_id: programId, weekdays, items: validItems }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3 p-3 bg-muted/20 rounded-xl border border-border" data-testid="charge-form">
      <div>
        <label className="text-xs font-medium text-muted-foreground block mb-1">Ofen *</label>
        <select value={ovenId} onChange={e => setOvenId(e.target.value)}
          className="w-full h-9 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          data-testid="charge-oven-select">
          {ovens.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground block mb-1">Programm *</label>
        <select value={programId} onChange={e => setProgramId(e.target.value)}
          className="w-full h-9 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          data-testid="charge-program-select">
          {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground block mb-1.5">Wochentage *</label>
        <div className="flex flex-wrap gap-1.5">
          {WOCHENTAGE.map(({ key, short }) => (
            <button key={key} type="button" onClick={() => toggleDay(key)}
              className={`px-2.5 py-1 rounded-lg border text-xs font-semibold transition-colors ${
                weekdays.includes(key)
                  ? 'border-amber-500 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                  : 'border-border text-muted-foreground hover:border-amber-300'
              }`} data-testid={`weekday-${key}`}>
              {short}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground block mb-1.5">Produkte & Mengen</label>
        <ItemsEditor items={items} onChange={setItems} />
      </div>
      <div className="flex gap-2 pt-1">
        <Button onClick={handleSave} disabled={saving} size="sm"
          className="flex-1 bg-amber-500 hover:bg-amber-600 text-white gap-1.5"
          data-testid="charge-save-btn">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          {existingCharge ? 'Speichern' : 'Charge anlegen'}
        </Button>
        <Button variant="outline" size="sm" className="flex-1" onClick={onCancel}>Abbrechen</Button>
      </div>
    </div>
  );
}

// ── Sortierbare Charge-Zeile (DnD) ────────────────────────────────────────────
function SortableChargeRow({ charge, isAdmin, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: charge.id,
    data: { type: 'charge', ovenId: charge.oven_id, charge },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  const weekdayBadge = days =>
    days.map(d => d.slice(0,2).charAt(0).toUpperCase() + d.slice(0,2).slice(1)).join(', ');

  return (
    <div ref={setNodeRef} style={style}
      className="flex items-start gap-2 bg-amber-50/60 dark:bg-amber-900/10 border border-amber-200/60 rounded-xl px-3 py-2.5"
      data-testid={`charge-row-${charge.id}`}>
      <button {...listeners} {...attributes}
        className="mt-0.5 p-1 text-muted-foreground/40 hover:text-muted-foreground cursor-grab active:cursor-grabbing shrink-0 touch-none">
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-semibold shrink-0">
            {charge.oven_name}
          </span>
          <span className="text-sm font-semibold">{charge.program_name}</span>
          <span className="text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 rounded font-medium">
            {weekdayBadge(charge.weekdays)}
          </span>
        </div>
        {charge.items?.length > 0 && (
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {charge.items.map(i => `${i.quantity ? i.quantity + '× ' : ''}${i.product_name}`).join(', ')}
          </p>
        )}
      </div>
      <button onClick={() => onEdit(charge)}
        className="p-1 text-muted-foreground hover:text-foreground shrink-0">
        <Pencil className="h-3.5 w-3.5" />
      </button>
      {isAdmin && (
        <button onClick={() => onDelete(charge.id)}
          className="p-1 text-muted-foreground hover:text-destructive shrink-0"
          data-testid={`delete-charge-${charge.id}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// ── Droppable Ofen-Abschnitt ──────────────────────────────────────────────────
function OvenDropZone({ ovenId }) {
  const { setNodeRef, isOver } = useDroppable({ id: `oven-${ovenId}` });
  return (
    <div ref={setNodeRef}
      className={`min-h-[2rem] rounded-lg border-2 border-dashed transition-colors ${
        isOver ? 'border-amber-400 bg-amber-50/40 dark:bg-amber-900/10' : 'border-transparent'
      }`} />
  );
}

// ── Chargen-Verwaltung mit DnD (alle Rollen: anlegen+bearbeiten; Admin: löschen) ──
function ChargenSection({ locationId, ovens, programs, charges, isAdmin, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editCharge, setEditCharge] = useState(null);
  const [localCharges, setLocalCharges] = useState(charges);
  const [activeCharge, setActiveCharge] = useState(null);

  useEffect(() => { setLocalCharges(charges); }, [charges]);

  const canAddCharge = ovens.length > 0 && programs.length > 0;
  const weekdayBadge = days =>
    days.map(d => d.slice(0,2).charAt(0).toUpperCase() + d.slice(0,2).slice(1)).join(', ');

  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  }));

  // Chargen nach Ofen gruppieren, sortiert
  const chargesByOven = useMemo(() => {
    const byOven = {};
    ovens.forEach(o => { byOven[o.id] = []; });
    localCharges.forEach(c => {
      if (!byOven[c.oven_id]) byOven[c.oven_id] = [];
      byOven[c.oven_id].push(c);
    });
    Object.values(byOven).forEach(arr =>
      arr.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    );
    return byOven;
  }, [localCharges, ovens]);

  const handleDragStart = ({ active }) => {
    const charge = localCharges.find(c => c.id === active.id);
    setActiveCharge(charge || null);
  };

  const handleDragOver = useCallback(({ active, over }) => {
    if (!over) return;
    const activeCharge = localCharges.find(c => c.id === active.id);
    if (!activeCharge) return;
    // Cross-oven visual feedback: move charge into target oven group
    let targetOvenId = activeCharge.oven_id;
    const overCharge = localCharges.find(c => c.id === over.id);
    if (overCharge) targetOvenId = overCharge.oven_id;
    else if (typeof over.id === 'string' && over.id.startsWith('oven-'))
      targetOvenId = over.id.replace('oven-', '');
    if (targetOvenId !== activeCharge.oven_id) {
      const targetOven = ovens.find(o => o.id === targetOvenId);
      setLocalCharges(prev => prev.map(c =>
        c.id === active.id
          ? { ...c, oven_id: targetOvenId, oven_name: targetOven?.name || c.oven_name }
          : c
      ));
    }
  }, [localCharges, ovens]);

  const handleDragEnd = useCallback(async ({ active, over }) => {
    setActiveCharge(null);
    if (!over) { setLocalCharges(charges); return; }

    const movedCharge = localCharges.find(c => c.id === active.id);
    if (!movedCharge) return;

    let targetOvenId = movedCharge.oven_id;
    const overCharge = localCharges.find(c => c.id === over.id);
    if (overCharge) targetOvenId = overCharge.oven_id;
    else if (typeof over.id === 'string' && over.id.startsWith('oven-'))
      targetOvenId = over.id.replace('oven-', '');

    const targetOvenCharges = localCharges
      .filter(c => c.oven_id === targetOvenId)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

    let newOrder;
    const oldIndex = targetOvenCharges.findIndex(c => c.id === active.id);
    const newIndex = overCharge ? targetOvenCharges.findIndex(c => c.id === over.id) : targetOvenCharges.length - 1;

    if (oldIndex !== -1 && movedCharge.oven_id === (localCharges.find(c => c.id === active.id)?.oven_id)) {
      newOrder = arrayMove(targetOvenCharges, oldIndex, newIndex < 0 ? targetOvenCharges.length - 1 : newIndex);
    } else {
      newOrder = [...targetOvenCharges.filter(c => c.id !== active.id)];
      const insertAt = newIndex >= 0 ? newIndex : newOrder.length;
      newOrder.splice(insertAt, 0, movedCharge);
    }

    // Optimistic update
    const targetOven = ovens.find(o => o.id === targetOvenId);
    const updated = localCharges.map(c => {
      const idx = newOrder.findIndex(nc => nc.id === c.id);
      if (idx !== -1) return { ...c, sort_order: idx, oven_id: targetOvenId, oven_name: targetOven?.name || c.oven_name };
      return c;
    });
    setLocalCharges(updated);

    try {
      const originalOvenId = charges.find(c => c.id === active.id)?.oven_id;
      await axios.put(`${API}/api/baking/charges/reorder`, {
        location_id: locationId,
        charges: newOrder.map((c, idx) => ({
          id: c.id,
          sort_order: idx,
          oven_id: c.oven_id !== originalOvenId ? c.oven_id : undefined,
        })),
      }, { headers: authH() });
      onRefresh();
    } catch {
      toast.error('Fehler beim Umsortieren');
      setLocalCharges(charges);
    }
  }, [localCharges, charges, ovens, locationId, onRefresh]);

  const saveCharge = async (formData) => {
    try {
      if (editCharge) {
        await axios.put(`${API}/api/baking/charges/${editCharge.id}`, formData, { headers: authH() });
        toast.success('Charge aktualisiert');
      } else {
        await axios.post(`${API}/api/baking/charges`, { location_id: locationId, ...formData }, { headers: authH() });
        toast.success('Charge angelegt');
      }
      setShowAdd(false); setEditCharge(null); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); throw e; }
  };

  const deleteCharge = async id => {
    if (!window.confirm('Charge wirklich löschen?')) return;
    try { await axios.delete(`${API}/api/baking/charges/${id}`, { headers: authH() }); onRefresh(); }
    catch { toast.error('Fehler beim Löschen'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold flex items-center gap-2">
          <Flame className="h-4 w-4 text-amber-500" />
          Chargen verwalten
          {localCharges.length > 0 && (
            <span className="text-xs font-normal text-muted-foreground">· {localCharges.length} gesamt</span>
          )}
        </p>
        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs"
          onClick={() => { setShowAdd(true); setEditCharge(null); }}
          disabled={!canAddCharge}
          title={!canAddCharge ? 'Zuerst Öfen und Programme konfigurieren' : ''}
          data-testid="add-charge-btn">
          <Plus className="h-3.5 w-3.5" /> Neue Charge
        </Button>
      </div>

      {!canAddCharge && (
        <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl p-3">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>Zuerst Öfen und Programme konfigurieren (Admin/SL-Bereich).</span>
        </div>
      )}

      {showAdd && !editCharge && (
        <ChargeForm ovens={ovens} programs={programs} locationId={locationId}
          onSave={saveCharge} onCancel={() => setShowAdd(false)} />
      )}

      {editCharge && (
        <ChargeForm ovens={ovens} programs={programs} locationId={locationId}
          existingCharge={editCharge}
          onSave={saveCharge} onCancel={() => setEditCharge(null)} />
      )}

      {localCharges.length === 0 && !showAdd ? (
        <p className="text-xs text-muted-foreground italic text-center py-3">Noch keine Chargen konfiguriert</p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCorners}
          onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
          <div className="space-y-4">
            {ovens.map(oven => {
              const ovenCharges = chargesByOven[oven.id] || [];
              return (
                <div key={oven.id}>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <Flame className="h-3 w-3 text-amber-500" /> {oven.name}
                  </p>
                  <SortableContext items={ovenCharges.map(c => c.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-1.5">
                      {ovenCharges.map(charge => (
                        <SortableChargeRow key={charge.id} charge={charge} isAdmin={isAdmin}
                          onEdit={c => { setEditCharge(c); setShowAdd(false); }}
                          onDelete={deleteCharge} />
                      ))}
                    </div>
                  </SortableContext>
                  <OvenDropZone ovenId={oven.id} />
                </div>
              );
            })}
          </div>
          <DragOverlay>
            {activeCharge && (
              <div className="flex items-center gap-2 bg-background border border-amber-400 rounded-xl px-3 py-2.5 shadow-lg opacity-95">
                <GripVertical className="h-4 w-4 text-amber-500" />
                <span className="text-sm font-semibold">{activeCharge.program_name}</span>
                <span className="text-xs text-muted-foreground">{activeCharge.oven_name}</span>
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}

// ── Sortierbare Program-Zeile ─────────────────────────────────────────────────
function SortableProgramRow({ prog, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: prog.id,
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  return (
    <div ref={setNodeRef} style={style}
      className="flex items-center gap-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-full px-2.5 py-1">
      <button {...listeners} {...attributes} className="cursor-grab active:cursor-grabbing text-amber-400 touch-none">
        <GripVertical className="h-3 w-3" />
      </button>
      <Tag className="h-3 w-3 text-amber-600 shrink-0" />
      <span className="text-xs font-medium text-amber-800 dark:text-amber-300">{prog.name}</span>
      <button onClick={() => onEdit(prog)} className="text-amber-400 hover:text-amber-600 ml-0.5">
        <Pencil className="h-3 w-3" />
      </button>
      <button onClick={() => onDelete(prog.id, prog.name)} className="text-amber-400 hover:text-destructive ml-0.5">
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

// ── Konfig-Panel: Öfen + Programme (DnD) + Settings ──────────────────────────
function KonfigPanel({ locationId, ovens, programs, settings, onRefresh }) {
  const [newOvenName, setNewOvenName] = useState('');
  const [newProgName, setNewProgName] = useState('');
  const [savingOven, setSavingOven] = useState(false);
  const [savingProg, setSavingProg] = useState(false);
  const [editProgram, setEditProgram] = useState(null);
  const [editProgName, setEditProgName] = useState('');
  const [localPrograms, setLocalPrograms] = useState(programs);
  const [activeProgId, setActiveProgId] = useState(null);
  const [resetStart, setResetStart] = useState(settings?.reset_start || '04:00');
  const [resetEnd, setResetEnd]   = useState(settings?.reset_end   || '12:00');
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => { setLocalPrograms(programs); }, [programs]);
  useEffect(() => {
    setResetStart(settings?.reset_start || '04:00');
    setResetEnd(settings?.reset_end || '12:00');
  }, [settings]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // Öfen
  const addOven = async () => {
    if (!newOvenName.trim()) return;
    setSavingOven(true);
    try {
      await axios.post(`${API}/api/baking/ovens`, { location_id: locationId, name: newOvenName.trim() }, { headers: authH() });
      setNewOvenName(''); toast.success('Ofen angelegt'); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSavingOven(false); }
  };
  const deleteOven = async (id, name) => {
    if (!window.confirm(`Ofen "${name}" löschen?`)) return;
    try { await axios.delete(`${API}/api/baking/ovens/${id}`, { headers: authH() }); onRefresh(); }
    catch { toast.error('Fehler'); }
  };

  // Programme
  const addProg = async () => {
    if (!newProgName.trim()) return;
    setSavingProg(true);
    try {
      await axios.post(`${API}/api/baking/programs`, { location_id: locationId, name: newProgName.trim() }, { headers: authH() });
      setNewProgName(''); toast.success('Programm angelegt'); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSavingProg(false); }
  };
  const deleteProg = async (id, name) => {
    if (!window.confirm(`Programm "${name}" löschen?`)) return;
    try { await axios.delete(`${API}/api/baking/programs/${id}`, { headers: authH() }); onRefresh(); }
    catch { toast.error('Fehler'); }
  };
  const saveEditProg = async () => {
    if (!editProgName.trim() || !editProgram) return;
    const editedId   = editProgram.id;
    const editedName = editProgName.trim();
    try {
      await axios.put(`${API}/api/baking/programs/${editedId}`, { name: editedName }, { headers: authH() });
      toast.success('Programm aktualisiert');
      // 1. Edit sofort schließen
      setEditProgram(null);
      setEditProgName('');
      // 2. Optimistisches Update — Name direkt in localPrograms setzen OHNE vollen Refresh.
      //    → DnD-Kontext bekommt eine saubere Einzel-Änderung statt einem kompletten Array-Ersatz.
      //    Das verhindert die Race Condition wo onRefresh() → setLocalPrograms() die DnD
      //    handleProgDragEnd-Dependency neu triggert und Pencil-Klicks kurzfristig schluckt.
      setLocalPrograms(prev => prev.map(p => p.id === editedId ? { ...p, name: editedName } : p));
      // 3. Hintergrund-Sync leicht verzögert, damit React erst den optimistischen Update
      //    verarbeitet bevor der volle Refresh die DnD-Dependency-Chain neu aufbaut.
      setTimeout(onRefresh, 60);
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  // Program DnD
  const handleProgDragEnd = useCallback(async ({ active, over }) => {
    setActiveProgId(null);
    if (!over || active.id === over.id) return;
    const oldIdx = localPrograms.findIndex(p => p.id === active.id);
    const newIdx = localPrograms.findIndex(p => p.id === over.id);
    const reordered = arrayMove(localPrograms, oldIdx, newIdx).map((p, i) => ({ ...p, sort_order: i }));
    setLocalPrograms(reordered);
    try {
      await axios.put(`${API}/api/baking/programs/reorder`, {
        location_id: locationId,
        programs: reordered.map((p, i) => ({ id: p.id, sort_order: i })),
      }, { headers: authH() });
      onRefresh();
    } catch { toast.error('Fehler beim Umsortieren'); setLocalPrograms(programs); }
  }, [localPrograms, programs, locationId, onRefresh]);

  // Settings speichern
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/api/baking/settings`, { location_id: locationId, reset_start: resetStart, reset_end: resetEnd }, { headers: authH() });
      toast.success('Einstellungen gespeichert'); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSavingSettings(false); }
  };

  return (
    <div className="space-y-5">
      {/* Öfen */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Öfen</p>
        <div className="flex flex-wrap gap-2 mb-2">
          {ovens.map(o => (
            <div key={o.id} className="flex items-center gap-1.5 bg-muted/40 rounded-lg px-2.5 py-1.5">
              <Flame className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-sm font-medium">{o.name}</span>
              <button onClick={() => deleteOven(o.id, o.name)} className="text-muted-foreground/40 hover:text-destructive ml-0.5">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input value={newOvenName} onChange={e => setNewOvenName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addOven()}
            placeholder="z.B. Ofen oben" className="h-8 text-sm flex-1"
            data-testid="new-oven-input" />
          <Button size="sm" onClick={addOven} disabled={savingOven || !newOvenName.trim()}
            className="bg-amber-500 hover:bg-amber-600 text-white gap-1 shrink-0 h-8">
            <Plus className="h-3.5 w-3.5" /> Anlegen
          </Button>
        </div>
      </div>

      {/* Programme mit DnD + Edit */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Programme</p>
        {editProgram && (
          <div className="flex gap-2 mb-2 p-2 bg-muted/30 rounded-lg">
            <Input value={editProgName} onChange={e => setEditProgName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && saveEditProg()}
              className="h-8 text-sm flex-1" autoFocus />
            <Button size="sm" onClick={saveEditProg} className="h-8 bg-amber-500 hover:bg-amber-600 text-white">
              <Check className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditProgram(null)} className="h-8">
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
        <DndContext sensors={sensors} onDragStart={({ active }) => setActiveProgId(active.id)} onDragEnd={handleProgDragEnd}>
          <SortableContext items={localPrograms.map(p => p.id)} strategy={verticalListSortingStrategy}>
            <div className="flex flex-wrap gap-2 mb-2">
              {localPrograms.map(p => (
                <SortableProgramRow key={p.id} prog={p}
                  onEdit={p => { setEditProgram(p); setEditProgName(p.name); }}
                  onDelete={deleteProg} />
              ))}
            </div>
          </SortableContext>
          <DragOverlay>
            {activeProgId && (() => {
              const p = localPrograms.find(x => x.id === activeProgId);
              return p ? (
                <div className="flex items-center gap-1.5 bg-background border border-amber-400 rounded-full px-2.5 py-1 shadow-lg">
                  <Tag className="h-3 w-3 text-amber-600" />
                  <span className="text-xs font-medium">{p.name}</span>
                </div>
              ) : null;
            })()}
          </DragOverlay>
        </DndContext>
        <div className="flex gap-2">
          <Input value={newProgName} onChange={e => setNewProgName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addProg()}
            placeholder="z.B. Brötchen Voll" className="h-8 text-sm flex-1"
            data-testid="new-program-input" />
          <Button size="sm" onClick={addProg} disabled={savingProg || !newProgName.trim()}
            className="bg-amber-500 hover:bg-amber-600 text-white gap-1 shrink-0 h-8">
            <Plus className="h-3.5 w-3.5" /> Anlegen
          </Button>
        </div>
      </div>

      {/* Tageswechsel-Einstellungen */}
      <div className="border-t border-border pt-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Tageswechsel-Zeiten</p>
        <p className="text-xs text-muted-foreground mb-3">
          Ab "Start" beginnt der neue Backtag (grüne Haken werden zurückgesetzt).
          Bis "Ende" werden alle verbliebenen Haken spätestens zurückgesetzt.
        </p>
        <div className="flex gap-3 flex-wrap items-end">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Tageswechsel Start</label>
            <input type="time" value={resetStart} onChange={e => setResetStart(e.target.value)}
              className="h-9 rounded-lg border border-border bg-background px-3 text-sm font-mono"
              data-testid="reset-start-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Tageswechsel Ende</label>
            <input type="time" value={resetEnd} onChange={e => setResetEnd(e.target.value)}
              className="h-9 rounded-lg border border-border bg-background px-3 text-sm font-mono"
              data-testid="reset-end-input" />
          </div>
          <Button size="sm" onClick={saveSettings} disabled={savingSettings}
            className="bg-amber-500 hover:bg-amber-600 text-white gap-1 h-9"
            data-testid="save-settings-btn">
            {savingSettings ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Tagesansicht mit Fertig-Haken ─────────────────────────────────────────────
function TagesansichtBacken({ charges, weekdayName, completions, bakingDayStart, forceReset, onComplete, onUncomplete }) {
  if (!charges.length) return (
    <div className="text-center py-8 text-muted-foreground" data-testid="no-charges-today">
      <Flame className="h-8 w-8 mx-auto mb-2 opacity-30" />
      <p className="font-medium text-sm">Heute keine Chargen</p>
      <p className="text-xs mt-1">Für {WOCHENTAG_DE[weekdayName] || weekdayName} sind keine Backchargen konfiguriert.</p>
    </div>
  );

  const byOven = {};
  charges.forEach(c => {
    if (!byOven[c.oven_name]) byOven[c.oven_name] = [];
    byOven[c.oven_name].push(c);
  });

  return (
    <div className="space-y-4" data-testid="tagesansicht-backen">
      {Object.entries(byOven).map(([ovenName, ovenCharges]) => (
        <div key={ovenName}>
          <div className="flex items-center gap-2 mb-2">
            <Flame className="h-4 w-4 text-amber-500 shrink-0" />
            <span className="font-bold text-sm">{ovenName}</span>
          </div>
          <div className="space-y-2 ml-2">
            {ovenCharges.map((charge, i) => {
              const completion = completions.find(c => c.charge_id === charge.id);
              const isDone = completion && isCompletionCurrent(completion.completed_at, bakingDayStart, forceReset);
              return (
                <div key={charge.id}
                  className={`rounded-xl border px-4 py-3 space-y-2 transition-colors ${
                    isDone
                      ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
                      : 'border-border bg-card'
                  }`}
                  data-testid={`today-charge-${charge.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full shrink-0 ${
                        isDone
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                          : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                      }`}>
                        {i + 1}. Charge
                      </span>
                      <span className="font-semibold text-sm">{charge.program_name}</span>
                    </div>
                    {/* Fertig-Button */}
                    <button
                      onClick={() => isDone ? onUncomplete(charge.id) : onComplete(charge.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all shrink-0 ${
                        isDone
                          ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-300 dark:hover:bg-green-900/60'
                          : 'bg-muted text-muted-foreground hover:bg-amber-100 hover:text-amber-700 dark:hover:bg-amber-900/20 dark:hover:text-amber-300'
                      }`}
                      data-testid={`fertig-btn-${charge.id}`}>
                      {isDone
                        ? <><CheckCircle2 className="h-3.5 w-3.5" /> Fertig</>
                        : <><Check className="h-3.5 w-3.5" /> Als fertig markieren</>}
                    </button>
                  </div>
                  {charge.items?.length > 0 && (
                    <div className="space-y-0.5 pl-1">
                      {charge.items.map((item, j) => (
                        <div key={j} className="flex items-center gap-2 text-sm">
                          <span className="text-muted-foreground">·</span>
                          {item.quantity && <span className={`font-semibold shrink-0 ${isDone ? 'text-green-600 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>{item.quantity}×</span>}
                          <span className={isDone ? 'text-green-700 dark:text-green-300' : ''}>{item.product_name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {isDone && completion && (
                    <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400 pt-0.5">
                      <Clock className="h-3 w-3 shrink-0" />
                      <span>Erledigt von <strong>{completion.completed_by_name}</strong> um {fmtTime(completion.completed_at)} Uhr</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Haupt-Export ──────────────────────────────────────────────────────────────
export function BakenTab() {
  const { user } = useAuth();
  const isAdmin = ['admin', 'stationsleitung', 'superadmin'].includes(user?.role);

  const [locations, setLocations] = useState([]);
  const [selectedLocId, setSelectedLocId] = useState('');
  const [ovens, setOvens] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [allCharges, setAllCharges] = useState([]);
  const [todayData, setTodayData] = useState({ weekday: '', charges: [] });
  const [completions, setCompletions] = useState([]);
  const [bakingSettings, setBakingSettings] = useState({ reset_start: '04:00', reset_end: '12:00' });
  const [loading, setLoading] = useState(true);
  const [showKonfig, setShowKonfig] = useState(false);

  // Baking-Day-Berechnungen (live, keine Caching-Probleme da nur beim Rendern berechnet)
  const bakingDayStart = useMemo(() => parseBakingDayStart(bakingSettings.reset_start), [bakingSettings.reset_start]);
  const forceReset = useMemo(() => isForceReset(bakingSettings.reset_end), [bakingSettings.reset_end]);

  useEffect(() => {
    axios.get(`${API}/api/user/locations`, { headers: authH() })
      .then(r => {
        const locs = r.data || [];
        setLocations(locs);
        if (locs.length > 0) {
          const own = user?.location_ids?.length ? locs.find(l => user.location_ids.includes(l.id)) : null;
          setSelectedLocId(own?.id || locs[0].id);
        }
      }).catch(() => toast.error('Standorte konnten nicht geladen werden'));
  }, [user?.id]);

  const fetchAll = useCallback(async () => {
    if (!selectedLocId) return;
    setLoading(true);
    try {
      const [ovRes, pgRes, chargesRes, todayRes, compRes, settingsRes] = await Promise.all([
        axios.get(`${API}/api/baking/ovens?location_id=${selectedLocId}`, { headers: authH() }),
        axios.get(`${API}/api/baking/programs?location_id=${selectedLocId}`, { headers: authH() }),
        axios.get(`${API}/api/baking/charges?location_id=${selectedLocId}`, { headers: authH() }),
        axios.get(`${API}/api/baking/charges/today?location_id=${selectedLocId}`, { headers: authH() }),
        axios.get(`${API}/api/baking/completions?location_id=${selectedLocId}`, { headers: authH() }),
        axios.get(`${API}/api/baking/settings?location_id=${selectedLocId}`, { headers: authH() }),
      ]);
      setOvens(ovRes.data || []);
      setPrograms(pgRes.data || []);
      setAllCharges(chargesRes.data || []);
      setTodayData(todayRes.data || { weekday: '', charges: [] });
      setCompletions(compRes.data || []);
      setBakingSettings(settingsRes.data || { reset_start: '04:00', reset_end: '12:00' });
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  }, [selectedLocId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleComplete = async (chargeId) => {
    try {
      const comp = await axios.post(`${API}/api/baking/charges/${chargeId}/complete`, {}, { headers: authH() });
      setCompletions(prev => {
        const filtered = prev.filter(c => c.charge_id !== chargeId);
        return [...filtered, comp.data];
      });
    } catch { toast.error('Fehler'); }
  };

  const handleUncomplete = async (chargeId) => {
    try {
      await axios.delete(`${API}/api/baking/charges/${chargeId}/complete`, { headers: authH() });
      setCompletions(prev => prev.filter(c => c.charge_id !== chargeId));
    } catch { toast.error('Fehler'); }
  };

  if (loading && locations.length === 0) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-5 pb-8" data-testid="baken-tab">
      <LocationSelector locations={locations} value={selectedLocId} onChange={setSelectedLocId} />

      {/* Tagesansicht mit Fertig-Haken */}
      <div>
        <p className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Flame className="h-4 w-4 text-amber-500" />
          Was backen wir heute?
          {todayData.weekday && (
            <span className="text-xs font-normal text-muted-foreground">
              ({WOCHENTAG_DE[todayData.weekday] || todayData.weekday})
            </span>
          )}
          {forceReset && (
            <span className="text-xs text-orange-500 font-normal">· Tageswechsel aktiv</span>
          )}
        </p>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <TagesansichtBacken
            charges={todayData.charges}
            weekdayName={todayData.weekday}
            completions={completions}
            bakingDayStart={bakingDayStart}
            forceReset={forceReset}
            onComplete={handleComplete}
            onUncomplete={handleUncomplete}
          />
        )}
      </div>

      {/* Chargen verwalten (alle Rollen) */}
      {!loading && (
        <div className="border border-border rounded-xl p-4" data-testid="chargen-section">
          <ChargenSection
            locationId={selectedLocId}
            ovens={ovens}
            programs={programs}
            charges={allCharges}
            isAdmin={isAdmin}
            onRefresh={fetchAll}
          />
        </div>
      )}

      {/* Öfen & Programme konfigurieren (nur Admin/SL) */}
      {isAdmin && (
        <div className="border border-border rounded-xl overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors"
            onClick={() => setShowKonfig(v => !v)}
            data-testid="baking-konfig-toggle">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Öfen & Programme konfigurieren (Admin/SL)</span>
            </div>
            {showKonfig ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </button>
          {showKonfig && (
            <div className="p-4 border-t border-border">
              <KonfigPanel
                locationId={selectedLocId}
                ovens={ovens}
                programs={programs}
                settings={bakingSettings}
                onRefresh={fetchAll}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BakenTab;
